import { useState, useEffect, useMemo } from 'react'
import { useMutation, useQuery } from '@apollo/client'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { CREATE_VARIANT_OPTION_GROUP, UPDATE_VARIANT_OPTION_GROUP } from '../graphql/mutations'
import { GET_PRODUCT_VARIANTS, GET_OPTION_GROUPS } from '../graphql/product-queries'
import { UpdateVariantOptionGroupData, VariantOptionGroup, ProductVariant } from '../types/product'
import toast from 'react-hot-toast'
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline'

interface VariantOptionGroupFormProps {
  variantOptionGroup?: VariantOptionGroup
  isEdit?: boolean
}

const VariantOptionGroupForm = ({ variantOptionGroup, isEdit = false }: VariantOptionGroupFormProps) => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  
  const [formData, setFormData] = useState<{
    product_variant_ids: number[]
    option_group_id: number
    sort_order: number | null
  }>({
    product_variant_ids: variantOptionGroup?.product_variant_id ? [variantOptionGroup.product_variant_id] : [],
    option_group_id: variantOptionGroup?.option_group_id || 0,
    sort_order: variantOptionGroup?.sort_order || null
  })

  const [searchTerm, setSearchTerm] = useState('')
  const [errors, setErrors] = useState<Record<string, string>>({})

  const { data: variantsData } = useQuery(GET_PRODUCT_VARIANTS, {
    variables: { filters: { active: true } }
  })

  const { data: optionGroupsData } = useQuery(GET_OPTION_GROUPS, {
    variables: { filters: { active: true } }
  })

  const [createVariantOptionGroup, { loading: createLoading }] = useMutation(CREATE_VARIANT_OPTION_GROUP)

  const [updateVariantOptionGroup, { loading: updateLoading }] = useMutation(UPDATE_VARIANT_OPTION_GROUP, {
    onCompleted: () => {
      toast.success('Variant option group connection updated successfully')
      navigate('/variant-option-groups')
    },
    onError: (error) => {
      toast.error(`Failed to update connection: ${error.message}`)
    }
  })

  useEffect(() => {
    // Set default values from URL params
    const urlVariantId = searchParams.get('variantId')
    const urlOptionGroupId = searchParams.get('optionGroupId')
    
    if (urlVariantId && !isEdit) {
      setFormData(prev => ({ ...prev, product_variant_ids: [parseInt(urlVariantId)] }))
    }
    if (urlOptionGroupId) {
      setFormData(prev => ({ ...prev, option_group_id: parseInt(urlOptionGroupId) }))
    }
  }, [searchParams, isEdit])

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {}

    if (formData.product_variant_ids.length === 0) {
      newErrors.product_variant_ids = 'At least one product variant is required'
    }

    if (!formData.option_group_id || formData.option_group_id <= 0) {
      newErrors.option_group_id = 'Option group is required'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!validateForm()) {
      return
    }

    try {
      if (isEdit && variantOptionGroup) {
        await updateVariantOptionGroup({
          variables: { 
            id: Number(variantOptionGroup.id),
            data: {
              product_variant_id: parseInt(formData.product_variant_ids[0].toString()),
              option_group_id: formData.option_group_id,
              sort_order: formData.sort_order
            } as UpdateVariantOptionGroupData 
          }
        })
      } else {
        // Bulk create
        const creationPromises = formData.product_variant_ids.map(variantId => 
          createVariantOptionGroup({
            variables: { 
              data: {
                product_variant_id: parseInt(variantId.toString()),
                option_group_id: formData.option_group_id,
                sort_order: formData.sort_order
              } 
            }
          })
        )

        await Promise.all(creationPromises)
        toast.success(`${formData.product_variant_ids.length} connection(s) created successfully`)
        navigate('/variant-option-groups')
      }
    } catch (error: any) {
      toast.error(`Operation failed: ${error.message}`)
    }
  }

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }))
    }
  }

  const toggleVariant = (variantId: any) => {
    const id = parseInt(variantId.toString())
    setFormData(prev => {
      const isSelected = prev.product_variant_ids.some(existingId => parseInt(existingId.toString()) === id)
      const newIds = isSelected 
        ? prev.product_variant_ids.filter(existingId => parseInt(existingId.toString()) !== id)
        : [...prev.product_variant_ids, id]
      
      return { ...prev, product_variant_ids: newIds }
    })
    if (errors.product_variant_ids) {
      setErrors(prev => ({ ...prev, product_variant_ids: '' }))
    }
  }

  const selectAll = (ids: any[]) => {
    const numericIds = ids.map(id => parseInt(id.toString()))
    setFormData(prev => {
      const currentIds = prev.product_variant_ids.map(id => parseInt(id.toString()))
      return { ...prev, product_variant_ids: Array.from(new Set([...currentIds, ...numericIds])) }
    })
  }

  const deselectAll = (ids: any[]) => {
    const numericIds = ids.map(id => parseInt(id.toString()))
    setFormData(prev => ({ 
      ...prev, 
      product_variant_ids: prev.product_variant_ids.filter(id => !numericIds.includes(parseInt(id.toString()))) 
    }))
  }

  const variants = variantsData?.productVariants || []
  const optionGroups = optionGroupsData?.optionGroups || []
  
  const filteredVariants = useMemo(() => {
    return variants.filter((v: ProductVariant) => {
      const searchLower = searchTerm.toLowerCase()
      return v.name.toLowerCase().includes(searchLower) || v.product?.name.toLowerCase().includes(searchLower)
    })
  }, [variants, searchTerm])

  const groupedVariants = useMemo(() => {
    const groups: Record<string, ProductVariant[]> = {}
    filteredVariants.forEach((v: ProductVariant) => {
      const productName = v.product?.name || 'Unknown Product'
      if (!groups[productName]) groups[productName] = []
      groups[productName].push(v)
    })
    return groups
  }, [filteredVariants])

  const loading = createLoading || updateLoading

  return (
    <div className="max-w-4xl mx-auto">
      <div className="card">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-medium text-gray-900">
            {isEdit ? 'Edit Variant Option Group Connection' : 'Connect Variants to Option Group'}
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            {isEdit 
              ? 'Update the connection between a product variant and option group'
              : 'Link one or more product variants to an option group'
            }
          </p>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-8">
          {/* Option Group Selection */}
          <div className="max-w-md">
            <label htmlFor="option_group_id" className="block text-sm font-medium text-gray-700 mb-2">
              Step 1: Select Option Group *
            </label>
            <select
              id="option_group_id"
              value={formData.option_group_id}
              onChange={(e) => handleInputChange('option_group_id', parseInt(e.target.value))}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                errors.option_group_id ? 'border-red-300' : 'border-gray-300'
              }`}
              disabled={loading || isEdit}
            >
              <option value={0}>Select an option group</option>
              {optionGroups.map((group: any) => (
                <option key={group.id} value={group.id}>
                  {group.name} (Min: {group.min_select}, Max: {group.max_select})
                </option>
              ))}
            </select>
            {errors.option_group_id && (
              <p className="mt-1 text-sm text-red-600">{errors.option_group_id}</p>
            )}
          </div>

          {/* Product Variant Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-4">
              Step 2: Select Product Variants *
            </label>
            
            {isEdit ? (
              <div className="max-w-md">
                <select
                  value={formData.product_variant_ids[0] || 0}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 cursor-not-allowed"
                  disabled
                >
                  <option value={variantOptionGroup?.product_variant_id}>
                    {variantOptionGroup?.product_variant?.name} - {variantOptionGroup?.product_variant?.product?.name}
                  </option>
                </select>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex items-center justify-between gap-4">
                  <div className="relative flex-1 max-w-sm">
                    <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search variants..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-9 pr-4 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => selectAll(filteredVariants.map((v: any) => v.id))}
                      className="text-xs font-medium text-primary-600 hover:text-primary-700"
                    >
                      Select All Filtered
                    </button>
                    <span className="text-gray-300">|</span>
                    <button
                      type="button"
                      onClick={() => deselectAll(variants.map((v: any) => v.id))}
                      className="text-xs font-medium text-gray-500 hover:text-gray-600"
                    >
                      Clear Selection
                    </button>
                  </div>
                </div>

                <div className="border border-gray-200 rounded-lg max-h-[400px] overflow-y-auto">
                  {Object.keys(groupedVariants).length === 0 ? (
                    <div className="p-8 text-center text-gray-500">
                      No variants found matching your search
                    </div>
                  ) : (
                    Object.entries(groupedVariants).map(([productName, productVariants]) => (
                      <div key={productName} className="border-b border-gray-100 last:border-0">
                        <div className="bg-gray-50 px-4 py-2 flex items-center justify-between sticky top-0 z-10">
                          <span className="text-xs font-bold text-gray-600 uppercase tracking-wider">
                            {productName}
                          </span>
                          <button
                            type="button"
                            onClick={() => {
                              const productVariantIds = productVariants.map(v => parseInt(v.id.toString()))
                              const allSelected = productVariantIds.every(id => 
                                formData.product_variant_ids.some(selectedId => parseInt(selectedId.toString()) === id)
                              )
                              
                              if (allSelected) {
                                deselectAll(productVariantIds)
                              } else {
                                selectAll(productVariantIds)
                              }
                            }}
                            className="text-[10px] font-medium text-primary-600 hover:text-primary-700"
                          >
                            {productVariants.every(v => 
                              formData.product_variant_ids.some(selectedId => parseInt(selectedId.toString()) === parseInt(v.id.toString()))
                            ) ? 'Deselect All' : 'Select All'}
                          </button>
                        </div>
                        <div className="divide-y divide-gray-50">
                          {productVariants.map((variant) => {
                            const vId = parseInt(variant.id.toString())
                            const isChecked = formData.product_variant_ids.some(id => parseInt(id.toString()) === vId)
                            
                            return (
                              <label
                                key={variant.id}
                                className={`flex items-center px-4 py-3 hover:bg-primary-50 cursor-pointer transition-colors ${
                                  isChecked ? 'bg-primary-50' : ''
                                }`}
                              >
                                <input
                                  type="checkbox"
                                  checked={isChecked}
                                  onChange={() => toggleVariant(variant.id)}
                                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded cursor-pointer"
                                />
                                <div className="ml-3">
                                  <span className="text-sm font-medium text-gray-900">{variant.name}</span>
                                  <span className="ml-2 text-xs text-gray-500">
                                    €{(variant.price_cents / 100).toFixed(2)}
                                    {variant.sku && ` • SKU: ${variant.sku}`}
                                  </span>
                                </div>
                              </label>
                            )
                          })}
                        </div>
                      </div>
                    ))
                  )}
                </div>
                {errors.product_variant_ids && (
                  <p className="mt-1 text-sm text-red-600">{errors.product_variant_ids}</p>
                )}
                <p className="text-xs text-gray-500">
                  {formData.product_variant_ids.length} variant(s) selected
                </p>
              </div>
            )}
          </div>

          {/* Sort Order */}
          <div className="max-w-md">
            <label htmlFor="sort_order" className="block text-sm font-medium text-gray-700 mb-2">
              Sort Order (Optional)
            </label>
            <input
              type="number"
              id="sort_order"
              value={formData.sort_order || ''}
              onChange={(e) => handleInputChange('sort_order', e.target.value ? parseInt(e.target.value) : null)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="e.g. 1"
              disabled={loading}
            />
            <p className="mt-1 text-sm text-gray-500">
              Affects display order of option groups for these variants.
            </p>
          </div>

          {/* Form Actions */}
          <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={() => navigate('/variant-option-groups')}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={loading || formData.product_variant_ids.length === 0 || formData.option_group_id === 0}
            >
              {loading ? (
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  {isEdit ? 'Updating...' : `Creating ${formData.product_variant_ids.length} Connection(s)...`}
                </div>
              ) : (
                isEdit ? 'Update Connection' : `Create ${formData.product_variant_ids.length} Connection(s)`
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default VariantOptionGroupForm

